/**
 * Custom Wishlist Module v3.0
 * Storage: localStorage (per-customer namespaced), customer metafield sync via API (logged-in).
 *
 * Why v3: v2 treated each device's localStorage as the source of truth and
 * pushed the whole local list to the server on every change (last-write-wins,
 * no merge). Combined with a one-shot blind "replace localStorage with server"
 * on load, devices drifted and whichever synced last clobbered the metafield.
 *
 * v3 reconciles on every load by MERGING server + local with last-write-wins
 * PER VARIANT, using per-item timestamps and tombstones so removals propagate
 * across devices instead of resurrecting. The backend stays a pure pass-through.
 *
 * Storage entry shapes (one array, stored both locally and in the metafield):
 *   live item:  { v: <variantId int>, h: "<handle>", t: <addedAt ms> }
 *   tombstone:  { v: <variantId int>, t: <removedAt ms>, d: 1 }
 *
 * Public get()/count()/has() expose only LIVE items as { v, h, t }, so the
 * drawer, wishlist page, header badge and variant-table hearts are unchanged.
 */
(function() {
  'use strict';

  var GUEST_KEY   = 'wishlist_v2';        // also the pre-v3 (un-namespaced) key
  var OLD_KEY     = 'wishlist_variants';  // pre-v2 format: { variantId: true }
  var TOMBSTONE_TTL = 1000 * 60 * 60 * 24 * 90; // prune deletions after 90 days

  var customerId = window.__wishlistCustomerId || null;
  // Logged-in customers get their own key so guest/other-customer data on a
  // shared browser can never leak into (or be pushed up as) their wishlist.
  var STORAGE_KEY = customerId ? (GUEST_KEY + '_c' + customerId) : GUEST_KEY;

  function now() { return Date.now(); }
  function entryTime(e) { return (e && typeof e.t === 'number') ? e.t : 0; }

  // --- Storage helpers (operate on the RAW array, tombstones included) ---

  function read() {
    try {
      var raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        var parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) return parsed;
      }
    } catch(e) {}
    return [];
  }

  function write(raw) {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(prune(raw))); } catch(e) {}
  }

  // Drop tombstones once they're old enough that every device has long since
  // reconciled the removal. Live items are never pruned.
  function prune(raw) {
    var cutoff = now() - TOMBSTONE_TTL;
    return raw.filter(function(e) { return !e.d || entryTime(e) >= cutoff; });
  }

  // Live items only, in the { v, h, t } shape consumers expect.
  function liveItems(raw) {
    return (raw || read()).filter(function(e) { return e && !e.d && e.v != null; });
  }

  // Last-write-wins union, keyed by variant id. For each variant the entry with
  // the newest timestamp wins, so a recent removal (tombstone) beats an older
  // add and a recent add beats an older removal. Absence is NOT a delete — only
  // a tombstone deletes — so adds a device has never seen are always preserved.
  function mergeRaw(a, b) {
    var byVariant = {};
    function consider(e) {
      if (!e || e.v == null) return;
      var key = parseInt(e.v, 10);
      var existing = byVariant[key];
      if (!existing || entryTime(e) > entryTime(existing)) byVariant[key] = e;
    }
    (a || []).forEach(consider);
    (b || []).forEach(consider);
    return Object.keys(byVariant).map(function(k) { return byVariant[k]; });
  }

  // --- Migration: fold any legacy keys into the current namespaced key ---

  function migrate() {
    var incoming = [];

    // pre-v2 format { variantId: true }
    try {
      var oldRaw = localStorage.getItem(OLD_KEY);
      if (oldRaw) {
        var obj = JSON.parse(oldRaw);
        Object.keys(obj).forEach(function(vid) {
          if (obj[vid]) incoming.push({ v: parseInt(vid, 10), h: '', t: now() });
        });
        localStorage.removeItem(OLD_KEY);
      }
    } catch(e) {}

    // pre-v3 un-namespaced wishlist_v2 (only relevant for logged-in users, whose
    // STORAGE_KEY now differs from GUEST_KEY). Preserves an existing list across
    // the upgrade and seeds the first reconcile.
    if (STORAGE_KEY !== GUEST_KEY) {
      try {
        var guestRaw = localStorage.getItem(GUEST_KEY);
        if (guestRaw) {
          var arr = JSON.parse(guestRaw);
          if (Array.isArray(arr)) {
            arr.forEach(function(e) {
              if (e && e.v != null) {
                incoming.push({ v: parseInt(e.v, 10), h: e.h || '', t: entryTime(e) || now(), d: e.d });
              }
            });
          }
          localStorage.removeItem(GUEST_KEY); // don't leak onto the next customer
        }
      } catch(e) {}
    }

    if (incoming.length) write(mergeRaw(read(), incoming));
  }

  // --- Remote sync (Railway API) ---
  //
  // Auth (2026-09-14): config.apiToken is PER-CUSTOMER — the value of the
  // customer's own custom.wishlist_token metafield, minted server-side by
  // POST /wishlist/bootstrap. First-ever logged-in load has no token yet:
  // we fire bootstrap (which writes the metafield and returns nothing) and
  // sync starts on the next page load. A 401 (secret rotated server-side)
  // re-runs bootstrap the same way. Sync is silent/local-only until then —
  // no data is lost, localStorage remains the working store.

  var syncTimer = null;

  function canSync() {
    var config = window.__wishlistConfig;
    return !!(config && config.apiUrl && config.apiToken && customerId);
  }

  function requestBootstrap() {
    var config = window.__wishlistConfig;
    if (!config || !config.apiUrl || !customerId) return;
    // Once per tab session — the metafield write is idempotent and the next
    // full page load picks the token up from Liquid anyway.
    try {
      if (sessionStorage.getItem('wishlist_bootstrap')) return;
      sessionStorage.setItem('wishlist_bootstrap', '1');
    } catch (e) {}
    fetch(config.apiUrl + '/wishlist/bootstrap', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ customer_id: customerId })
    }).catch(function() {});
  }

  function postSync(useKeepalive) {
    var config = window.__wishlistConfig;
    if (!canSync()) return;
    fetch(config.apiUrl + '/wishlist/sync', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Wishlist-Token': config.apiToken
      },
      // Send the full raw list, tombstones and timestamps included, so removals
      // and last-write-wins ordering survive the round trip to other devices.
      body: JSON.stringify({ customer_id: customerId, items: read() }),
      keepalive: !!useKeepalive
    }).then(function(resp) {
      if (resp && resp.status === 401) requestBootstrap();
    }).catch(function() {});
  }

  function syncToServer() {
    if (!canSync()) return;
    // Debounce: wait 1s after the last change before syncing. syncTimer MUST
    // be nulled when the timer fires: flushSync() treats a non-null syncTimer
    // as "a sync is still pending" and re-POSTs the local list on every page
    // hide/unload. Before this reset (bug caught by wishlist-sync-e2e.js,
    // 2026-09-14), any device that had ever synced would re-push its by-then
    // stale list on reload, wholesale-overwriting newer removals made on
    // other devices — resurrecting deleted items.
    clearTimeout(syncTimer);
    syncTimer = setTimeout(function() { syncTimer = null; postSync(false); }, 1000);
  }

  // Flush a pending debounced sync on page hide so the last change isn't lost.
  function flushSync() {
    if (!syncTimer) return;
    clearTimeout(syncTimer);
    syncTimer = null;
    postSync(true);
  }

  document.addEventListener('visibilitychange', function() {
    if (document.visibilityState === 'hidden') flushSync();
  });
  window.addEventListener('beforeunload', flushSync);

  // --- Public API ---

  function get() { return liveItems(); }

  function has(variantId) {
    var vid = parseInt(variantId, 10);
    return liveItems().some(function(item) { return item.v === vid; });
  }

  function add(variantId, handle) {
    var vid = parseInt(variantId, 10);
    var raw = read();
    if (liveItems(raw).some(function(item) { return item.v === vid; })) return;
    // Drop any prior entry (live or tombstone) for this variant, then add fresh.
    raw = raw.filter(function(e) { return parseInt(e.v, 10) !== vid; });
    raw.push({ v: vid, h: handle || '', t: now() });
    write(raw);
    dispatch('add', vid);
    syncToServer();
  }

  function remove(variantId) {
    var vid = parseInt(variantId, 10);
    var raw = read();
    if (!liveItems(raw).some(function(item) { return item.v === vid; })) return;
    // Replace the live entry with a tombstone so the removal propagates.
    raw = raw.filter(function(e) { return parseInt(e.v, 10) !== vid; });
    raw.push({ v: vid, t: now(), d: 1 });
    write(raw);
    dispatch('remove', vid);
    syncToServer();
  }

  function toggle(variantId, handle) {
    if (has(variantId)) { remove(variantId); return false; }
    add(variantId, handle);
    return true;
  }

  function count() { return liveItems().length; }

  function clear() {
    var live = liveItems();
    if (!live.length) return;
    var t = now();
    // Tombstone every current item so the clear propagates to other devices.
    var raw = read().filter(function(e) { return e.d; });
    live.forEach(function(item) { raw.push({ v: item.v, t: t, d: 1 }); });
    write(raw);
    dispatch('clear', null);
    syncToServer();
  }

  // --- Events ---

  function dispatch(action, variantId) {
    document.dispatchEvent(new CustomEvent('wishlist:changed', {
      detail: { action: action, variantId: variantId, count: count() }
    }));
  }

  // --- Count badge updates ---

  function updateBadges() {
    var c = count();
    var links = document.querySelectorAll('.site-header-wishlist__link');
    links.forEach(function(link) {
      if (c > 0) { link.classList.add('has-items'); }
      else { link.classList.remove('has-items'); }
    });
    var badges = document.querySelectorAll('[data-wishlist-count]');
    badges.forEach(function (badge) {
      if (c > 0) {
        badge.setAttribute('data-wishlist-count', String(c));
        badge.style.display = '';
        badge.classList.add('visible');
      } else {
        badge.setAttribute('data-wishlist-count', '');
        badge.classList.remove('visible');
      }
    });
  }

  document.addEventListener('wishlist:changed', updateBadges);

  // --- Init / reconcile ---

  migrate();

  // `ready` resolves once the initial server reconcile is done (or immediately
  // for guests / when the API isn't configured). Consumers that render a full
  // list on load (e.g. the wishlist page) should await this so they don't paint
  // a pre-reconcile snapshot.
  var resolveReady;
  var ready = new Promise(function(res) { resolveReady = res; });

  if (canSync()) {
    var config = window.__wishlistConfig;
    fetch(config.apiUrl + '/wishlist/' + customerId, {
      headers: { 'X-Wishlist-Token': config.apiToken },
      cache: 'no-store'
    })
    .then(function(resp) {
      if (resp.status === 401) { requestBootstrap(); return null; }
      return resp.ok ? resp.json() : null;
    })
    .then(function(data) {
      if (data && Array.isArray(data.items)) {
        var serverRaw = data.items;
        var merged = mergeRaw(serverRaw, read());
        write(merged);
        // Push the merged result back so the server converges on the union.
        // Only when it actually differs from what the server returned.
        if (liveItems(merged).length !== liveItems(serverRaw).length ||
            JSON.stringify(merged) !== JSON.stringify(prune(serverRaw))) {
          postSync(false);
        }
        dispatch('sync', null);
      }
    })
    .catch(function() {})
    .then(function() { updateBadges(); resolveReady(); });
  } else {
    // No server reconcile path yet: guest, API not configured, or a logged-in
    // customer whose per-customer token metafield hasn't been minted. The
    // inline metafield snapshot (rendered by Liquid) still merges in — it IS
    // the server state, just delivered with the page — so pre-bootstrap
    // customers keep their cross-device view; only the push-back waits for
    // the token.
    if (window.__wishlistData && Array.isArray(window.__wishlistData)) {
      write(mergeRaw(read(), window.__wishlistData));
    }
    if (customerId && window.__wishlistConfig && !window.__wishlistConfig.apiToken) {
      requestBootstrap();
    }
    resolveReady();
  }

  // Initial badge update after DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', updateBadges);
  } else {
    updateBadges();
  }

  // --- Expose global ---

  window.Wishlist = {
    get: get,
    has: has,
    add: add,
    remove: remove,
    toggle: toggle,
    count: count,
    clear: clear,
    ready: ready
  };
})();
